import { useEffect, useState, useCallback } from 'react';
import { useOfficeStore } from '../../officeStore';
import { useDocumentsStore, useEmlStore } from './store';
import { DocumentView } from './types';
import * as Sentry from '@sentry/react';
import { useIntl } from 'react-intl';
import messages from './messages';
import { sleep } from '../../utils/asyncUtils';
import { graphApiBaseUrl } from '../../config';
import { fetchWithRetry } from '../../utils/retryUtils';

interface GraphMessage {
  id: string;
  subject?: string;
  conversationId?: string;
  sender?: { emailAddress?: { name?: string; address?: string } };
  createdDateTime?: string;
  attachments?: GraphAttachment[];
}

interface GraphAttachment {
  '@odata.type':
    | '#microsoft.graph.fileAttachment'
    | '#microsoft.graph.itemAttachment';
  id: string;
  name?: string;
  contentType?: string;
  size?: number;
  contentBytes?: string;
  isInline?: boolean;
}

interface GraphPagedResponse<T> {
  value: T[];
  '@odata.nextLink'?: string;
}

const validFileEndings: string[] = [
  'pdf',
  'docx',
  'ppsx',
  'tif',
  'jpg',
  'jpeg',
  'png',
  'xlsx',
  'html',
  'gif',
  'txt',
];

export const useAttachmentDocuments = ({
  token,
  conversationId,
  updateLoading,
  includeEmlFile = true,
}: {
  token: string;
  conversationId: string;
  updateLoading: (loading: boolean) => void;
  includeEmlFile?: boolean;
}): {
  attachmentDocuments: DocumentView[];
} => {
  const {
    documents: attachmentDocuments,
    addDocument,
    removeDocument,
    clearSelection: clearDocuments,
  } = useDocumentsStore();

  const { addFile, clear: clearFiles } = useEmlStore();
  const [finishedFetching, setFinishedFetching] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const isOfficeInitialized = useOfficeStore(
    state => state.isOfficeInitialized,
  );
  const intl = useIntl();

  const resetState = useCallback(() => {
    setFinishedFetching(false);
    setIsFetching(false);
    clearDocuments();
    clearFiles();
  }, [clearDocuments, clearFiles]);

  useEffect(() => {
    resetState();
  }, [conversationId, resetState]);

  useEffect(() => {
    if (!includeEmlFile) {
      attachmentDocuments.forEach(doc => {
        if (!doc.attachment) {
          removeDocument(doc.id);
        }
      });
    }
  }, [includeEmlFile, attachmentDocuments, removeDocument]);

  useEffect(() => {
    if (
      !token ||
      !conversationId ||
      !isOfficeInitialized ||
      isFetching ||
      finishedFetching
    ) {
      return;
    }

    const fetchConversationData = async () => {
      setIsFetching(true);
      updateLoading(true);

      try {
        const encodedConversationId = encodeURIComponent(conversationId);

        let allMessages: GraphMessage[] = [];
        let nextLink:
          | string
          | undefined = `${graphApiBaseUrl}/me/messages?$filter=conversationId eq '${encodedConversationId}'&$expand=attachments($select=id,name,contentType,size,isInline)&$select=id,subject,sender,createdDateTime`; // Select needed fields
        while (nextLink) {
          // Error: Graph API error fetching messages (Status 400): {"error":{"code":"ErrorInvalidIdMalformed","message":"Id is malformed."}}
          const response = await fetchWithRetry(nextLink, {
            method: 'GET',
            headers: {
              Authorization: 'Bearer ' + token,
              Accept: 'application/json',
            },
          });

          if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
              Sentry.captureMessage(
                `Graph API Auth Error: ${response.status}`,
                { level: 'error', tags: { section: 'fetchMessages' } },
              );

            } else if (response.status === 429) {
              Sentry.captureMessage(
                `Graph API Throttling: ${response.status}. Max retries exceeded.`,
                { level: 'error', tags: { section: 'fetchMessages' } },
              );
            } else if (response.status === 503) {
              Sentry.captureMessage(
                `Graph API Unavailable: ${response.status}`,
                { level: 'warning', tags: { section: 'fetchMessages' } },
              );
            }
            const errorText = await response.text();
            throw new Error(
              `Graph API error fetching messages (Status ${response.status}): ${errorText}`,
            );
          }

          const pageData =
            (await response.json()) as GraphPagedResponse<GraphMessage>;

          allMessages = allMessages.concat(pageData.value || []);
          nextLink = pageData['@odata.nextLink'];
        }

        const emlProcessingPromises: Promise<void>[] = [];

        for (const message of allMessages) {
          if (message.attachments && message.attachments.length > 0) {
            message.attachments.forEach(attachment => {
              // Skip inline attachments and item attachments
              if (
                attachment.isInline ||
                attachment['@odata.type'] === '#microsoft.graph.itemAttachment'
              ) {
                return;
              }

              const extension =
                attachment.name?.split('.').pop()?.toLowerCase() || '';
              const isValidType = validFileEndings.includes(extension);

              const doc: DocumentView = {
                id: attachment.id,
                messageId: message.id,
                contentType: attachment.contentType,
                title: attachment.name || 'Untitled Attachment',
                content: null,
                isSelected: isValidType,
                attachment: true,
                invalidFiletype: !isValidType,
                md5: '',
              };
              addDocument(doc);
            });
          }

          // Add EML fetch to promises if requested
          if (includeEmlFile && message.id) {
            // Delay EML fetches slightly to avoid hammering the API
            await sleep(150); // Adjust delay as needed
            const emlFetchPromise = (async () => {
              const emlUrl = `${graphApiBaseUrl}/me/messages/${message.id}/$value`;
              try {
                const emlResponse = await fetchWithRetry(emlUrl, {
                  method: 'GET',
                  headers: { Authorization: 'Bearer ' + token },
                });

                if (!emlResponse.ok) {
                  // Specific handling for EML fetch errors
                  if (
                    emlResponse.status === 401 ||
                    emlResponse.status === 403
                  ) {
                    Sentry.captureMessage(
                      `Graph API Auth Error fetching EML: ${emlResponse.status}`,
                      {
                        level: 'error',
                        tags: { section: 'fetchEml', messageId: message.id },
                      },
                    );
                  } else if (emlResponse.status === 429) {
                    Sentry.captureMessage(
                      `Graph API Throttling fetching EML: ${emlResponse.status}. Max retries exceeded.`,
                      {
                        level: 'error',
                        tags: { section: 'fetchEml', messageId: message.id },
                      },
                    );
                  } else if (emlResponse.status === 503) {
                    Sentry.captureMessage(
                      `Graph API Unavailable fetching EML: ${emlResponse.status}`,
                      {
                        level: 'warning',
                        tags: { section: 'fetchEml', messageId: message.id },
                      },
                    );
                  } else {
                    const errorText = await emlResponse.text();
                    Sentry.captureMessage(
                      `Graph API Error fetching EML: ${emlResponse.status}`,
                      {
                        level: 'error',
                        tags: { section: 'fetchEml', messageId: message.id },
                        extra: { errorText },
                      },
                    );
                  }
                  return; // Don't proceed if EML fetch failed
                }

                const emlBlob = await emlResponse.blob();
                const fileName =
                  (message.subject || 'email_message') + '(EML).eml';
                const emlFile = new File([emlBlob], fileName, {
                  type: 'text/plain',
                });

                addFile(emlFile); // Add to EML store
              } catch (emlError) {
                Sentry.captureException(emlError, {
                  tags: { section: 'fetchEml', messageId: message.id },
                });
              }
            })();
            emlProcessingPromises.push(emlFetchPromise);
          }
        }

        if (emlProcessingPromises.length > 0) {
          await Promise.all(emlProcessingPromises);

          if (includeEmlFile && useEmlStore.getState().files.length > 0) {
            const emlViewId = `eml-bundle-${conversationId}`;
            if (
              !useDocumentsStore
                .getState()
                .documents.some(doc => doc.id === emlViewId)
            ) {
              addDocument({
                id: emlViewId,
                title: intl.formatMessage(messages.uploadEmailAsZipEml),
                messageId: null,
                contentType: 'application/zip',
                content: null,
                isSelected: true,
                attachment: false,
                invalidFiletype: false,
                md5: '',
              });
            }
          }
        }

        setFinishedFetching(true);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { section: 'useAttachmentDocumentsMainFetch' },
        });
        setFinishedFetching(true);
      } finally {
        setIsFetching(false);
        updateLoading(false);
      }
    };

    void fetchConversationData();
  }, [
    token,
    conversationId,
    isOfficeInitialized,
    isFetching,
    finishedFetching,
    includeEmlFile,
    updateLoading,
    addDocument,
    addFile,
    intl,
  ]);

  return { attachmentDocuments };
};
